import { useState, useEffect } from 'react';
import type { DrumInstrument, DrumsetConfig } from '../../types';
import { PRESET_DRUMSETS } from '../../config/drumKits';

interface PresetPickerProps {
  onPresetLoaded?: (instruments: DrumInstrument[]) => void;
  t: (key: string) => string;
  getThemeClass: (type: string) => string;
  currentInstruments?: DrumInstrument[];
}

export default function PresetPicker({ 
  onPresetLoaded, 
  t, 
  getThemeClass, 
  currentInstruments 
}: PresetPickerProps) {
  const [selectedPreset, setSelectedPreset] = useState<string>('');
  const [details, setDetails] = useState<DrumsetConfig | null>(null);

  // 當 currentInstruments 改變時，檢查是否匹配某個 preset kit
  useEffect(() => {
    if (!currentInstruments || currentInstruments.length === 0) {
      setSelectedPreset('');
      setDetails(null);
      return;
    }

    // 檢查當前的樂器配置是否匹配某個預設鼓組
    for (const [key, preset] of Object.entries(PRESET_DRUMSETS)) {
      if (preset.instruments.length === currentInstruments.length) {
        const isMatch = preset.instruments.every((presetInst, index) => {
          const currentInst = currentInstruments[index];
          return currentInst && 
                 presetInst.id === currentInst.id && 
                 presetInst.sampleId === currentInst.sampleId;
        });
        
        if (isMatch) {
          setSelectedPreset(key);
          setDetails(preset);
          return;
        }
      }
    }

    // 如果沒有匹配到任何預設，清空選擇
    setSelectedPreset('');
    setDetails(null);
  }, [currentInstruments]);

  const loadPreset = (presetKey: string): void => {
    if (!presetKey) {
      setSelectedPreset('');
      setDetails(null);
      return;
    }
    const preset = PRESET_DRUMSETS[presetKey as keyof typeof PRESET_DRUMSETS];
    if (preset) {
      setSelectedPreset(presetKey);
      setDetails(preset);
      if (onPresetLoaded) onPresetLoaded(preset.instruments);
    }
  };

  return (
    <div className={`p-4 rounded ${getThemeClass('card')} border ${getThemeClass('border')}`}> 
      {/* desktop layout */}
      <div className="hidden md:flex items-center space-x-4 mb-4">
        <div className="flex items-center space-x-2">
          <span className="text-2xl">🥁</span>
          <label className="text-lg font-semibold">{t('presetKits')}</label>
        </div>
        <select
          value={selectedPreset}
          onChange={(e) => loadPreset(e.target.value)}
          className={`border px-3 py-2 rounded-lg ${getThemeClass('border')} ${getThemeClass('select')} min-w-48`}
        >
          <option value="">{t('selectPresetKit') || '選擇預設鼓組...'}</option>
          {Object.entries(PRESET_DRUMSETS).map(([key, preset]) => (
            <option key={key} value={key}>
              {t(`drumKits.${key}`)} ({preset.instruments.length} {t('instruments') || '樂器'})
            </option>
          ))}
        </select>
      </div>

      {/* mobile layout */}
      <div className="md:hidden space-y-3 mb-4">
        <div className="flex items-center space-x-2">
          <span className="text-2xl">🥁</span>
          <label className="text-lg font-semibold">{t('presetKits')}</label>
        </div>
        <select
          value={selectedPreset}
          onChange={(e) => loadPreset(e.target.value)}
          className={`w-full border px-3 py-2 rounded-lg ${getThemeClass('border')} ${getThemeClass('select')}`}
        >
          <option value="">{t('selectPresetKit') || '選擇預設鼓組...'}</option>
          {Object.entries(PRESET_DRUMSETS).map(([key, preset]) => (
            <option key={key} value={key}>
              {t(`drumKits.${key}`)} ({preset.instruments.length})
            </option>
          ))}
        </select>
      </div>

      {details && (
        <div className={`p-3 rounded border ${getThemeClass('border')} ${getThemeClass('card')}`}> 
          <h4 className={`font-semibold mb-2 ${getThemeClass('text')}`}>📋 {t(`drumKits.${selectedPreset}`)}</h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
            {details.instruments.map((inst, i) => (
              <div key={i} className="flex items-center space-x-2">
                <div className={`w-4 h-4 rounded ${inst.color}`}></div>
                <span className="font-mono font-bold">{inst.id}</span>
                <span>{t(`drumTypes.${inst.type}`)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
} 